
import { dynamicQuestionContent, getCurrentResponseScore } from "@plura/shared/lead-forms";
import { trpc } from "@plura/shared/trpc";
import { LeadForm } from "@plura/shared/types";
import { useFormContext } from "react-hook-form";
import "./conversation-welcome-screen.css";

interface Props {
  id: string;
  title: string | null;
  caption?: string | null;
  tone: string | null;
  snippet: string[];
  about: string | null;
  purpose: string | null;
  type: 'welcome' | 'exit' | 'closed';
  editorMode?: boolean;
  previewMode?: boolean;
  onContentClick?: (fieldName: string, blockId: string) => void;
  hiddenFields?: {
    field: string;
    value: string;
  }[];
  leadForm: LeadForm;
}

const ConversationWelcomeScreen = (props: Props) => {
  // The closed screen is rendered outside ConversationWrapper's <Form>, so it
  // must not call useFormContext() — that throws and whitescreens the app.
  if (props.type === 'closed') {
    return <WelcomeScreenView {...props} title={props.title} userResponses={{}} />
  }
  return <WelcomeScreenWithFormContext {...props} />
}

const WelcomeScreenWithFormContext = (props: Props) => {
  const { title, type, about, purpose, tone, snippet } = props
  const { data: exitMessage } = trpc.leadForm.generateExitMessage.useQuery({
    about: about!,
    purpose: purpose!,
    tone: tone!,
    snippets: snippet,
  }, {
    enabled: type === 'exit',
    staleTime: Infinity,
  })
  const form = useFormContext();
  const { conversationId: _conversationId, ...userResponses } = form.getValues()
  const resolvedTitle = title ?? (type === 'exit' ? (exitMessage ?? null) : null)

  return (
    <WelcomeScreenView
      {...props}
      title={resolvedTitle}
      userResponses={userResponses}
    />
  )
}

const WelcomeScreenView = ({
  id,
  title,
  caption,
  editorMode,
  previewMode,
  onContentClick,
  hiddenFields,
  leadForm,
  userResponses,
}: Props & { userResponses: Record<string, unknown> }) => {
  const currentResponseScore = userResponses
    ? getCurrentResponseScore(userResponses as { [key: string]: string | number }, leadForm)
    : 0

  const handleContentClick = (field: 'content' | 'secondaryContent') => {
    if (!editorMode || !onContentClick) return
    onContentClick(field, id)
  }

  return (
    <div className="stepHeaderWrapper">
      {
        title && (
          <div
            className="titleWrapper"
            onClick={() => handleContentClick('content')}
            style={{ cursor: editorMode ? 'pointer' : 'default' }}
          >
            <h1 dangerouslySetInnerHTML={{
              __html: dynamicQuestionContent({
                content: title,
                score: currentResponseScore,
                editMode: editorMode,
                previewMode: previewMode ?? false,
                hiddenFields,
                journeyItems: leadForm.journeyItems ?? [],
                userResponses: userResponses as { [key: string]: string | number | boolean | null }
              })
            }}
            />
          </div>
        )
      }
      {
        caption && (
          <div
            className="captionWrapper"
            onClick={() => handleContentClick('secondaryContent')}
            style={{ cursor: editorMode && caption ? 'pointer' : 'default' }}
          >
            <h2 dangerouslySetInnerHTML={{
              __html: dynamicQuestionContent({
                content: caption,
                score: currentResponseScore,
                editMode: editorMode,
                previewMode: previewMode ?? false,
                hiddenFields,
                journeyItems: leadForm.journeyItems ?? [],
                userResponses: userResponses as { [key: string]: string | number | boolean | null }
              })
            }} />
          </div>
        )
      }
    </div>
  )
}

export default ConversationWelcomeScreen
